/**
 * A JavaScript module which provides functions for the current product context.
 * @module
 */

import {
	FetchConnectError,
	handleConnectError,
} from '../../../common/fetch-connect-error/index.tsx';
import type {
	ACJSHost,
	ConnectCallback,
	ExtensionParams,
	ErrorAttributes,
	ErrorWithStatusText,
} from '../../../types.tsx';
import { resolveByExtension } from '../iframe/host/content-resolver.tsx';
import type { AddonContextCache } from './types.tsx';
import { isValidExtensionOptionsForCache } from './util.tsx';

const CACHE_TIMEOUT = 30000; // only cache context for 30 seconds

// Map of add-on & module keys to addon context (license status, contextJwt and structured context)
const addonContextCache: Record<string, AddonContextCache> = {};

const getCacheKey = (extension: ExtensionParams): string => {
	if (extension.id && extension.id.length > 1) {
		return extension.id;
	}
	throw new Error('ACJS: cannot generate cache key for extension');
};

/**
 * format the full context that is returned in AP.context.getContext
 * this function is synchronous and assume the context cache is correct.
 * it should never be called directly.
 */
const _shapeFullContext = (cacheKey: string) => addonContextCache[cacheKey]?.context?.contextObj;

const isCacheExpired = (cachedAt: number) => CACHE_TIMEOUT + cachedAt < Date.now();

const shapeAddonContextCache = <S, J>(
	structuredContext: S,
	contextJwt: J,
): { cachedAt: number; contextJwt: J; contextObj: S } => ({
	cachedAt: Date.now(),
	contextJwt,
	contextObj: structuredContext,
});

// when an add-on is created, we prime the cache with the add-on specific context data.
const primeAddonContextCache = (addonData: ExtensionParams) => {
	const cacheKey = getCacheKey(addonData);
	/* if the cache isn't present
	 * or the cache entry doesn't have a promise
	 * and the entry is expired
	 */
	if (
		!addonContextCache[cacheKey] ||
		(!addonContextCache[cacheKey].promise &&
			addonContextCache[cacheKey].context &&
			isCacheExpired(addonContextCache[cacheKey].context.cachedAt))
	) {
		// eslint-disable-next-line no-param-reassign
		addonData.classifier = 'json';
		addonContextCache[cacheKey] = addonContextCache[cacheKey] || {};
		addonContextCache[cacheKey].promise = resolveByExtension(addonData, true).done(
			(promiseData) => {
				if (promiseData) {
					addonContextCache[cacheKey].context = shapeAddonContextCache(
						promiseData.options.structuredContext,
						promiseData.options.contextJwt,
					);
				}
			},
		);
	}
};

const checkNoJWT = (contextJwt: string) => {
	if (!contextJwt || contextJwt.length < 2) {
		throw new Error('ACJS: Cannot get token. Add-on does not support JWT authentication');
	}
};

const iframeCreated = (data: { extension: ExtensionParams }) => {
	if (isValidExtensionOptionsForCache(data.extension.options)) {
		const cacheKey = getCacheKey(data.extension);
		addonContextCache[cacheKey] = {
			context: shapeAddonContextCache(
				data.extension.options.structuredContext,
				data.extension.options.contextJwt,
			),
		};
	} else {
		primeAddonContextCache(data.extension);
	}
};

export const registerContextCache = (connectHost: ACJSHost) => {
	connectHost.onIframeEstablished(iframeCreated);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	connectHost.onIframeUnload((data: any) => {
		// kill the entry in the queue when the extension is unloaded as all handlers are destroyed.
		delete addonContextCache[getCacheKey(data.extension)];
	});
};

/**
 * Retrieves the current user context as a JWT token containing details such as space key, issue id, etc.
 * Throws an error if add-on does not support JWT authentication
 * @returns A promise that contains the token
 * @param callback - the callback that handles the response
 * @example
 * AP.context.getToken(function(token){
 *   console.log("JWT token string", token);
 * });
 */
export const getToken = (callback: ConnectCallback) =>
	new Promise((resolve, reject) => {
		// handle error in try-catch block or unhandled promise.
		const handleGetTokenError = (error: FetchConnectError, errorAttributes: ErrorAttributes = {}) =>
			handleConnectError('getToken', error, {
				extId: callback._context?.extension?.id,
				extKey: callback._context?.extension?.key,
				addonKey: callback._context?.extension?.addon_key,
				moduleKey: callback._context?.extension?.module_key,
				...errorAttributes,
			});

		const cacheKey = getCacheKey(callback._context.extension);
		// update cache values if needed, check for expiry
		primeAddonContextCache(callback._context.extension);

		const addonContextCachePromise = addonContextCache[cacheKey].promise;
		// if primed context promise has yet to return, wait for it before calling back
		if (addonContextCachePromise) {
			addonContextCachePromise
				.done((promiseData) => {
					// wrap in try catch as this can fail if the iframe has closed whilst the HTTP request is in progress.
					try {
						// move `checkNoJWT` into `try...catch` so we can log it better
						// and avoid unhandled rejected promised caught by global error handler `window.onerror`
						checkNoJWT(promiseData.options.contextJwt);

						resolve(promiseData.options.contextJwt);
					} catch (error) {
						const connectError = new FetchConnectError(
							'getToken fails',
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(error as ErrorWithStatusText).status,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							error as Error,
							// skip sending to Sentry because the volume of this error is too high,
							// but there is no customer reports of this error.
							true,
						);

						handleGetTokenError(connectError, {
							isErrorInResolveFunc: true,
						});
					}
				})
				.fail((error: ErrorWithStatusText) => {
					const defaultErrorMsg = 'ACJS: content resolver failed to get context jwt token';
					const errorText = error.message || error.statusText || defaultErrorMsg;
					const newError = new FetchConnectError(errorText, error.status, error);
					handleGetTokenError(newError);

					reject(defaultErrorMsg);
				})
				.always(() => {
					delete addonContextCache[cacheKey].promise;
				});
		} else {
			checkNoJWT(addonContextCache[cacheKey].context.contextJwt);
			resolve(addonContextCache[cacheKey].context.contextJwt);
		}
	});

getToken.returnsPromise = true;

/**
 * Retrieves the current user context containing details such as space key, issue id, etc.
 * @returns A promise that contains the context
 * @param callback - the callback that handles the response
 * @example
 * AP.context.getContext(function(response){
 *   console.log("Jira Issue Key", response.issue_key);
 *   console.log("Confluence page id", response.pageId);
 *   console.log("license status", response.license);
 * });
 */
export const getContext = (callback: ConnectCallback) =>
	new Promise((resolve, reject) => {
		// handle error in try-catch block or unhandled promise.
		const handleGetContextError = (error: FetchConnectError, errorAttrs: ErrorAttributes = {}) =>
			handleConnectError('getContext', error, {
				extId: callback._context?.extension?.id,
				extKey: callback._context?.extension?.key,
				addonKey: callback._context?.extension?.addon_key,
				moduleKey: callback._context?.extension?.module_key,
				...errorAttrs,
			});

		const cacheKey = getCacheKey(callback._context.extension);
		// update cache values if needed, check for expiry
		primeAddonContextCache(callback._context.extension);

		const addonContextCachePromise = addonContextCache[cacheKey].promise;
		// if primed context promise has yet to return, wait for it before calling back
		if (addonContextCachePromise) {
			addonContextCachePromise
				.done((_data) => {
					// wrap in try catch as this can fail if the iframe has closed whilst the HTTP request is in progress.
					try {
						resolve(_shapeFullContext(cacheKey));
					} catch (error) {
						const connectError = new FetchConnectError(
							'getContext fails',
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(error as ErrorWithStatusText).status,
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							error as Error,
						);
						handleGetContextError(connectError, {
							isErrorInResolveFunc: true,
						});
					}
				})
				.fail((error) => {
					const defaultErrorMsg = 'ACJS: content resolver failed to get context';
					const errorText = error.message || error.statusText || defaultErrorMsg;
					const connectError = new FetchConnectError(errorText, error.status, error);
					handleGetContextError(connectError);

					reject(defaultErrorMsg);
				})
				.always(() => {
					delete addonContextCache[cacheKey].promise;
				});
		} else {
			resolve(_shapeFullContext(cacheKey));
		}
	});

getContext.returnsPromise = true;
